import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Loader2, Briefcase, MapPin, DollarSign, Calendar, CheckCircle2, Clock, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  open:   { label: 'Açık',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closed: { label: 'Kapalı',     cls: 'bg-gray-50 text-gray-400 border-gray-200' },
  filled: { label: 'Tamamlandı', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
};

export default function FreelancerInvoices() {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/freelancer/my-applications`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      setApplications(res.data?.items || []);
      setTotal(res.data?.items?.length || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const filledApps = applications.filter(a => a.status === 'filled');
  const openApps = applications.filter(a => a.status !== 'filled');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Başvurularım & Faturalar</h1>
          <p className="text-gray-500 text-sm mt-0.5">Başvurduğun iş ilanları ve tamamlanan projeler</p>
        </div>
        <Link to="/job-board">
          <Button className="bg-red-600 hover:bg-red-700 text-white gap-2">
            <Briefcase className="w-4 h-4" />İş İlanlarına Bak
          </Button>
        </Link>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Toplam Başvuru', value: total, icon: FileText, color: 'bg-blue-50 text-blue-600' },
          { label: 'Devam Eden', value: openApps.length, icon: Clock, color: 'bg-amber-50 text-amber-600' },
          { label: 'Tamamlanan', value: filledApps.length, icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-black text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        </div>
      ) : applications.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
          <Briefcase className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <h3 className="font-bold text-gray-900 mb-1">Henüz başvuru yok</h3>
          <p className="text-gray-500 text-sm mb-5">İş İlanları panosuna git ve sana uygun projeye başvur.</p>
          <Link to="/job-board">
            <Button className="bg-red-600 hover:bg-red-700 text-white">İş İlanlarını Gör →</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Active Applications */}
          {openApps.length > 0 && (
            <div>
              <h2 className="text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />Aktif Başvurular
              </h2>
              <div className="space-y-3">
                {openApps.map(app => (
                  <ApplicationCard key={app.id} app={app} />
                ))}
              </div>
            </div>
          )}

          {/* Completed */}
          {filledApps.length > 0 && (
            <div>
              <h2 className="text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />Tamamlanan Projeler
              </h2>
              <div className="space-y-3">
                {filledApps.map(app => (
                  <ApplicationCard key={app.id} app={app} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ApplicationCard({ app }) {
  const st = STATUS_STYLES[app.status] || STATUS_STYLES.open;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start justify-between gap-4 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="font-bold text-gray-900 text-sm">{app.title}</p>
          <Badge className={`text-[10px] border ${st.cls}`}>{st.label}</Badge>
        </div>
        <p className="text-xs text-gray-500 mb-2">{app.poster_name}</p>
        <div className="flex flex-wrap gap-3 text-xs text-gray-400">
          {app.location && (
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{app.location}</span>
          )}
          {app.budget && (
            <span className="flex items-center gap-1 text-emerald-600 font-semibold">
              <DollarSign className="w-3 h-3" />{app.budget} {app.budget_currency}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />{new Date(app.created_at).toLocaleDateString('tr-TR')}
          </span>
        </div>
      </div>
    </div>
  );
}
